/**
 * FinanceiroDrePage — DRE Gerencial REAL (Task #124, consome /api/v2/dre).
 *
 * Antes: mock dreMesAtual/ytd/12m + PeriodToggle mes/ytd/12m.
 * Depois: dados reais Omie classificados (#121e), toggle Competência/Caixa,
 * seletor de mês, 16 linhas + 7 calculadas + D11 informativo, e drill-down
 * D1-D10 → aba Despesas.
 *
 * R6 (ΔMtM) mostra "Em breve" — fonte #121f ainda não existe.
 * Seletor de período é MVP (mês); trimestre/ano/custom ficam pra depois.
 */
import { useMemo, useState } from "react";
import { KpiCard } from "../components/ui/KpiCard";
import { RegimeToggle, type Regime } from "../components/ui/RegimeToggle";
import { DreGerencialTable } from "../components/financeiro/DreGerencialTable";
import { Loading } from "../components/Loading";
import { useDreV2 } from "../hooks/useDreV2";
import { useDrePdfPublisher } from "../contexts/DrePdfContext";
import { pct } from "../format";
import { fmtBRL } from "../utils/formatters";

function currentMesRef(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

interface Props {
  /** Drill-down: clicar numa linha D1-D10 navega pra aba Despesas com a
   * linha pré-filtrada (lifted state em App.tsx). */
  onDrillDownDespesa?: (linhaCodigo: string) => void;
}

export function FinanceiroDrePage({ onDrillDownDespesa }: Props = {}) {
  const [regime, setRegime] = useState<Regime>("competencia");
  const [mesRef, setMesRef] = useState<string>(currentMesRef());

  const { data, isLoading, error } = useDreV2({ regime, periodo: "mensal", mesRef });

  // Task #186: publica o dado em tela pro export PDF do TopBar (ancestral).
  // Enquanto carrega/erro publica null → botão "Relatório PDF" desabilita.
  const pdfPayload = useMemo(
    () => (data ? { data, regime, mesRef } : null),
    [data, regime, mesRef],
  );
  useDrePdfPublisher(pdfPayload);

  const calc = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of data?.calculadas ?? []) m.set(c.codigo, c.valor_atual);
    return m;
  }, [data]);

  const receitaLiquida = calc.get("RECEITA_LIQUIDA") ?? 0;
  const margemBruta = calc.get("MARGEM_BRUTA") ?? 0;
  const ebitda = calc.get("EBITDA") ?? 0;
  const lucroLiquido = calc.get("LUCRO_LIQUIDO") ?? 0;
  const ebitdaMargem = receitaLiquida !== 0 ? ebitda / receitaLiquida : NaN;
  const lucroMargem = receitaLiquida !== 0 ? lucroLiquido / receitaLiquida : NaN;

  return (
    <div data-testid="financeiro-dre-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-white font-sans">DRE Gerencial</h1>
          <p className="text-white/50 text-sm mt-1">
            Visão escalonada — Receita → EBITDA → Lucro Líquido. Dados reais Omie
            classificados por linha DRE.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="month"
            value={mesRef}
            onChange={(e) => setMesRef(e.target.value)}
            data-testid="dre-mes-picker"
            aria-label="Mês de referência"
            className="bg-indra-azul-deep border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white/80 [color-scheme:dark]"
          />
          <RegimeToggle value={regime} onChange={setRegime} />
        </div>
      </div>

      {error ? (
        <div
          className="rounded-xl border border-indra-negativo/30 bg-indra-negativo/10 p-6 text-sm text-white/80"
          data-testid="dre-error"
        >
          Não foi possível carregar o DRE Gerencial. Verifique a conexão e tente
          novamente.
        </div>
      ) : !data || isLoading ? (
        <div className="flex items-center justify-center py-20" data-testid="dre-loading">
          <Loading label="Carregando DRE…" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="Receita Líquida"
              value={fmtBRL(receitaLiquida, { abbreviate: true })}
              highlight
            />
            <KpiCard
              label="Margem Bruta"
              value={fmtBRL(margemBruta, { abbreviate: true })}
              highlight
            />
            <KpiCard
              label="EBITDA"
              value={fmtBRL(ebitda, { abbreviate: true })}
              sub={Number.isNaN(ebitdaMargem) ? undefined : `margem ${pct(ebitdaMargem)}`}
              subTone={ebitda >= 0 ? "positive" : "negative"}
              highlight
            />
            <KpiCard
              label="Lucro Líquido"
              value={fmtBRL(lucroLiquido, { abbreviate: true })}
              sub={Number.isNaN(lucroMargem) ? undefined : `margem ${pct(lucroMargem)}`}
              subTone={lucroLiquido >= 0 ? "positive" : "negative"}
              highlight
            />
          </div>

          <DreGerencialTable dre={data} onDrillDown={onDrillDownDespesa} />

          <p className="text-[11px] text-white/40 mt-6 leading-relaxed max-w-3xl">
            Regime <strong>{regime === "competencia" ? "Competência" : "Caixa"}</strong>.
            {regime === "caixa" && (
              <>
                {" "}Caixa = títulos <strong>quitados por status</strong>, datados pela baixa
                quando a fonte informa (hoje: aproximados pelo vencimento — o Omie não entrega
                data de baixa). Não é extrato bancário.
              </>
            )}{" "}
            Cobertura de classificação {data.header.cobertura_classificacao_pct.toFixed(1)}% dos
            lançamentos Omie. R6 (Resultado MtM) aguarda a fonte de dados (#121f). D11 (mútuos
            com partes relacionadas) é informativo — fora do resultado contábil, vira saída de
            caixa no regime Caixa.
          </p>
        </>
      )}
    </div>
  );
}
